import { useEffect, useRef } from "react";

/**
 * AutoTextarea
 * ------------
 * Drop-in replacement for <input> / <textarea> that grows with its content
 * so the entire text is always visible. Min-height stays at 40 px (≈ one line),
 * grows in 22-px steps. No internal scrollbar, no truncation.
 */
export function AutoTextarea({ value, onChange, placeholder, className = "", testid, minRows = 1, ...rest }) {
  const ref = useRef(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    // Reset height so we measure the real scrollHeight when the user is deleting text
    el.style.height = "auto";
    el.style.height = `${Math.max(40, el.scrollHeight)}px`;
  };

  // Resize whenever the value changes externally (e.g. dictation)
  useEffect(() => { resize(); }, [value]);
  // Also resize on initial mount in case the content is pre-filled
  useEffect(() => { resize(); }, []);

  return (
    <textarea
      ref={ref}
      data-testid={testid}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => { onChange?.(e); resize(); }}
      rows={minRows}
      className={`block w-full resize-none overflow-hidden leading-[1.4] ${className}`}
      style={{ minHeight: 40 }}
      {...rest}
    />
  );
}

/**
 * formatEuro(value) → "9.800,00"
 * Always two decimals, German locale (dot for thousands, comma for decimals).
 * No currency symbol – the input field has the € label next to it.
 */
export function formatEuro(value) {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? "").replace(",", "."));
  if (!isFinite(n)) return "";
  return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

/**
 * Parse a German-formatted euro string back into a number.
 * Accepts "9.800,00", "9800.00", "9800,00", "9800", "" → returns number or null.
 */
export function parseEuro(text) {
  if (text === null || text === undefined) return null;
  const s = String(text).trim();
  if (!s) return null;
  // Remove thousand separators (dots only if they're separators), then convert comma to dot
  // German format: 9.800,00 → 9800.00
  const cleaned = s.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(cleaned);
  return isFinite(n) ? n : null;
}
